// src/redux/registerFormReducer.ts
import { formatDateTimeLocal, getBaseUrl } from '../../Utils'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { TabType } from './NTSP_StatPortal'
import { fail } from 'assert'
import { FieldErrors } from 'react-hook-form'
import { createSlice } from '@reduxjs/toolkit'
import moment from 'moment'

const initialState = {
    timezone: '',
    wasCompletedOnLoad: false, //FP-14292 - flag to indicate if the form was already in Completed status on load, used to determine if CPT codes are required for provider notes when outcome is Completed, since we only want to require CPT codes for cases that were marked Completed during the current session (i.e. not already Completed on load)
}
export interface NTSPDevices {
    _key: number
    fk_locaction: number // Foreign key to the location
    DeviceName: string
    URL: string // e.g., "Hospital", "Clinic", etc.
}

export interface NTSPElement {
    _key: number
    FriendlyName: string
    ETLMatchValue:string
    FacilityName?: string
    facilityCode?: string
    TZCode?: string
    ServiceLine?: string
    SiteStatus?: string
    LocationDevice?: NTSPDevices[]
    SymptomCategory?: string
    SymptomType?: string
    Title?:string
    StaffTitle?:string
    StaffPhoneNumber?:string
    IsHistory:boolean
    IsEEG?:boolean;
    ServiceLineEnum?: number
    IsNeurohospitalist?:boolean;
    IsAllowFollowUp?:boolean;
    fk_Symptom:number;
    DisplayOrder:number;
    IsPSFS?:boolean;
    FollowUpServiceLine?:string;
}

export interface ApiDTO_NTSPUI_ElementList {
    Outcome: NTSPElement[]
    Facilities: NTSPElement[]
    PatientLocations: NTSPElement[]
    ConsultServices: NTSPElement[]
    Diagnosis: NTSPElement[]
    Thrombolytic: NTSPElement[]
    Sex: NTSPElement[]
    CallerPosition: NTSPElement[]
    Consultants: NTSPElement[]
    PatientFetcherProcId: number
    FacilityFetcherProcId: number;
    HandOffCriteria: NTSPElement[]
    FollowUp: NTSPElement[]
    Symptoms: NTSPElement[]
    EEGType: NTSPElement[]
    PerformedBy: NTSPElement[]
    Contact: NTSPElement[]
    ServiceTitleMapping: { [key: string]: string | number }[]
    ConsultantTypes:NTSPElement[]
    ASDGiven:NTSPElement[]
    Consultants24Hr:NTSPElement[]
    PatientEduFollowUp: NTSPElement[]
    SymptomServiceMapping:NTSPElement[]
    ConsultServiceMetadataId:number
    Impacts: NTSPElement[]  //FP-14209 - NTSP Stat Portal - Process Issues Tab
    TypeOfIssue: NTSPElement[] //FP-14209 - NTSP Stat Portal - Process Issues Tab
    IssueStatus: NTSPElement[] //FP-14209 - NTSP Stat Portal - Process Issues Tab
    // FP-14213: Post MRS Tracking tab elements
    MRSStatus: NTSPElement[]
    CallAttempts: NTSPElement[]
    CPTCodes: NTSPElement[]
}

export interface ApiDTO_NTSPform {
    _Id: number // -1
    CaseId: string
    ts_Consult: string | null // consult date and time
    ts_LKW: string | null // LKW date and time
    ts_LKWDate?: string | null // LKW date in string format
    ts_LKWTime?: string | null // LKW time in string format
    flag_UnknownLKW: boolean
    fk_PatientLocation: number
    PatientFirstName: string
    PatientLastName: string
    PatientSSN: number | null //SSN
    fk_PatientSex: number
    PatientDOB: Date | null
    PatientAge: number
    fk_Consultant: number
    fk_Outcome: number //status dropdown on case data
    fk_ConsultService: number
    flag_DiscussedConference: boolean
    flag_InterestingCase: boolean
    fk_Thrombolytic: number
    ts_Thrombolytic: string | null
    ts_Call: string | null // Call ended
    //ts_LocalDate: Date | null
    //  ts_CallDate: string; // Call date in string format
    //  ts_CallTime: string; // Call time in string format
    CallerFullName?: string
    CallerPhoneNumber?: string
    fk_CallerPosition: number
    fk_Diagnosis: number // Diagnosis dropdown on case data
    fk_Facility: number // using this for va facility in patient details
    // TCSNumber?: string;
    Symptoms?: number[]
    FinCenter?: string
    // flag_HandOff2TSP: boolean;
    // flag_HandOff2Neuro: boolean;
    fk_HandOffCriteria: number
    flag_HandOff2NTSPConsult: boolean
    ts_HandOff: string | null // Hand-off date and time
    // ts_HandOffDate?: string; // Hand-off date in string format
    // ts_HandOffTime?: string; // Hand-off time in string format
    Notes?: DTO_NTSPCaseNote[] // newly added to display older notes it would be easy if backend sends a string with username: note
    ProviderNotes?: ProviderNoteGroup[] //List of provider notes as per all cases (parent,followups of the current cases,current case)
    _providerNotesEdited?: boolean // frontend-only flag to include edited provider notes in save payload
    _providerNoteEditMode?: boolean // frontend-only flag: NewNote text is editing an existing provider note
    _editedProviderNotesPayload?: ProviderNoteGroup[] //FP-14292 frontend-only: minimal edited provider note payload for save
    NewNote: string // for frontend use
    ExamCompleted:boolean;//for frontend use
    fk_CreatedBy: number // should this be used for consult type in nurse form
    tzCode?: string // Timezone code, e.g., "America/New_York"
    ts_RowCreated?: string | null // Timestamp when the row was created
    ts_RowUpdated?: string | null // Timestamp when the row was updated
    // flag_SiteDidNotPickUp?:boolean
    ProviderCaringName: string
    fk_FollowUp: number
    fk_FollowUps: number[]
    ts_EDArrivalTime: string | null
    fk_EEGType: number
    fk_UpdatedBy: number
    ts_WakeUpStroke: string | null
    ts_WakeUpStrokeDate?: string | null // Wake Up Stroke date in string format
    ts_WakeUpStrokeTime?: string | null // Wake Up Stroke time in string format
    HighlightsPlan?: string
    SiteContactInfo?: string
    flag_DiagnosisDiscussed: boolean
    ts_DiagnosisTime: string | null
    fk_Contact: number
    ts_EducationTime: string | null
    EducationNotes?: string
    ts_Facilitytime: string | null
    fk_ConsultantType:number
    ParentCaseId?:string | null
    MergedIntoCaseId?:string | null
    fk_ASD:number
    fk_followUpConsultant:number
    ts_FollowUpHandoff:string | null
    ts_EDArrivalDateOnly?:string | null
    ts_EDArrivalTimeOnly?:string | null
    flag_TEC?:boolean
    flag_TNC?:boolean
    fk_FollowupNeeded?:number
    fk_PtEduFollowUp?:number
    //FP-14209 Process Issues fields
    fk_ImpactOfIssue?: number
    fk_TypeOfIssue?: number
    fk_IssueStatus?: number
    TicketNumber?: string
    IssueDescription?: string
    // FP-14213: Post MRS Tracking fields
    fk_MRSFormStatus?: number | null // Form status for MRS tracking (e.g., Pending, Completed)
    flag_CallSuccessful?: boolean | null // Whether the call attempt was successful
    CurrentAttempt?: number | null // Current call attempt number (1, 2, or 3)
    ts_Attempt1?: string | null // Timestamp of first call attempt
    ts_Attempt2?: string | null // Timestamp of second call attempt
    ts_Attempt3?: string | null // Timestamp of third call attempt
    flag_IsPatientDeceased?: boolean | null // Whether the patient is deceased
    flag_Q1?: boolean | null // Answer to MRS question 1
    flag_Q2?: boolean | null // Answer to MRS question 2
    flag_Q3?: boolean | null // Answer to MRS question 3
    flag_Q4?: boolean | null // Answer to MRS question 4
    flag_Q5?: boolean | null // Answer to MRS question 5
    TotalScore?: number | null // Total MRS score (0-6)    
    ts_Discharge?: string | null // Discharge date and time
    MRSNote?: string // Additional notes related to MRS assessment
    fk_CPTCodes: number[]; // CPT code IDs for provider notes
    IsPSFSExists: boolean // Flag to indicate if PSFS form exists for the patient
}

export const initialFormState: ApiDTO_NTSPform = {
    _Id: -1,
    CaseId: '',
    fk_CreatedBy: -1,
    ts_Consult: null,
    ts_LKWDate: null,
    ts_LKWTime: null,
    ts_LKW: '',
    flag_UnknownLKW: false,
    fk_PatientLocation: -1,
    PatientFirstName: '',
    PatientLastName: '',
    PatientSSN: null,
    fk_PatientSex: -1,
    PatientAge: 0,
    PatientDOB: null,
    fk_Consultant: -1,
    fk_Outcome: 1,
    fk_ConsultService: -1,
    flag_DiscussedConference: false,
    flag_InterestingCase: false,
    fk_Thrombolytic: -1,
    ts_Thrombolytic: '',
    ts_Call: null, // Call ended
    // ts_CallDate: "", // Call date in string format
    // ts_CallTime: "", // Call time in string format
    ts_RowCreated: null,
    ts_RowUpdated: null,
    CallerFullName: '', // Full name of the caller
    CallerPhoneNumber: '',
    fk_Diagnosis: -1, // Diagnosis dropdown on case data
    fk_CallerPosition: -1,
    fk_Facility: -1,
    // TCSNumber: "",
    Symptoms: [],
    FinCenter: '',
    // flag_HandOff2TSP: false,
    // flag_HandOff2Neuro: false,
    fk_HandOffCriteria: -1,
    flag_HandOff2NTSPConsult: false,
    // ts_HandOffDate: "", // Hand-off date and time
    // ts_HandOffTime: "",
    ts_HandOff: null, // Hand-off date and time
    Notes: [],
    ProviderNotes:[],
    NewNote: '', // for frontend use
    ExamCompleted:false,//for frontend use
    fk_CPTCodes: [], // CPT code IDs for provider notes
    //ts_LocalDate: null,
    ProviderCaringName: '',
    fk_FollowUps: [-1],
    fk_FollowUp: -1,
    ts_EDArrivalTime: '',
    fk_EEGType: -1,
    fk_UpdatedBy: -1,
    ts_WakeUpStroke: '',
    ts_WakeUpStrokeDate: '',
    ts_WakeUpStrokeTime: '',
    HighlightsPlan: '',
    SiteContactInfo: '',
    flag_DiagnosisDiscussed: false,
    ts_DiagnosisTime: '',
    fk_Contact: -1,
    ts_EducationTime: null,
    EducationNotes: '',
    ts_Facilitytime: null,
    fk_ConsultantType:-1,
    ParentCaseId:null,
    fk_ASD:-1,
    fk_followUpConsultant: -1,
    ts_FollowUpHandoff: null,
    ts_EDArrivalDateOnly:null,
    ts_EDArrivalTimeOnly:null,
    flag_TEC:false,
    flag_TNC:false,
    fk_FollowupNeeded: 1, //by default assume follow-up is needed
    fk_PtEduFollowUp: -1,
    //FP-14209 Process Issues fields
    fk_ImpactOfIssue: -1,
    fk_TypeOfIssue: -1,
    fk_IssueStatus: 1, // Default to "Open"
    TicketNumber: '',
    IssueDescription: '',
    // FP-14213: Post MRS Tracking fields
    fk_MRSFormStatus: -1,
    flag_CallSuccessful: null,
    CurrentAttempt: -1,
    ts_Attempt1: null,
    ts_Attempt2: null,
    ts_Attempt3: null,
    flag_IsPatientDeceased: null,
    flag_Q1: null,
    flag_Q2: null,
    flag_Q3: null,
    flag_Q4: null,
    flag_Q5: null,
    TotalScore: null,
    ts_Discharge: null,
    MRSNote: '',  
    IsPSFSExists: false

}

export interface NTSP_StatPortalProps {
    NTSPElements?: ApiDTO_NTSPUI_ElementList
    form: ApiDTO_NTSPform
    setForm: React.Dispatch<React.SetStateAction<ApiDTO_NTSPform>>
    handleSave: (close?: boolean, formOverride?: ApiDTO_NTSPform) => void | Promise<void> //FP-14292 added optional form override parameter to allow passing a modified form for save without changing the current form in state, useful for saving staged provider note edits without applying them to the form until save is successful
    handleClear: () => void
    newCase?: () => void
    handleTabSwitch: (TabType: TabType) => void
    validationSchema?: { [key: string]: (value: any, ntspElements?: ApiDTO_NTSPUI_ElementList) => string }
    errors?: { [s: string]: string }
    setErrors?: (prev: any) => void
    isSaved?: boolean
    nurseNotes?: DTO_NTSPCaseNote[];
    providerNotes?: ProviderNoteGroup[];
    isLoadingNTSPElements?: boolean;
    selectedSymptoms?: {
        [s: string]: { label: string; value: string }[]
    }
    setSelectedSymptoms?: React.Dispatch<
        React.SetStateAction<{
            [s: string]: { label: string; value: string }[]
        }>
    >
    currentUserId?: number
}

export interface DTO_NTSPCaseNote {
    Id: number
    CaseId: string
    ts_CreationDateTime: string | Date
    PostedByUserId: number
    PostedByUserFullName: string
    Note: string
    tzCode: string
    NoteType:string
    ExamCompleted:boolean
    fk_CPTCodes: number[]
}
export interface ProviderNoteGroup {
    _Id: number;
    CaseId: string;
    ParentCaseId: string | null;
    fk_ConsultService: number;
    fk_FollowUp: number;
    ProviderCaseNotes: DTO_NTSPCaseNote[];
}

export interface LinkCaseRequest {
    CaseId: string;
    ParentCaseId: string;
}

export interface LinkCaseResponse {
    Success: boolean;
    Message: string;
    CaseId: string;
    ParentCaseId: string;
}

export interface MergeCaseRequest {
    SourceCaseId: string;
    TargetCaseId: string;
}

export interface MergeCaseResponse {
    Success: boolean;
    Message: string;
    SourceCaseId: string;
    TargetCaseId: string;
}

export const ntspSlice = createSlice({
    name: 'ntspSlice',
    initialState,
    reducers: {
        setTimezone(state, action) {
            state.timezone = action.payload
        },
        setWasCompletedOnLoad(state, action) {
            state.wasCompletedOnLoad = Boolean(action.payload)
        },
    },
})
export const NTSPForm = createApi({
    reducerPath: 'NTSP',
    baseQuery: fetchBaseQuery({ baseUrl: getBaseUrl() + 'API/v3' }), //The base API path
    endpoints: (build) => ({
        GetNTSPElements: build.mutation<ApiDTO_NTSPUI_ElementList, void>({
            query: () => ({
                url: '/ActivePanel/NTSPElements',
                method: 'GET',
            }),
            transformResponse: (response: any) => {
                return response.data
            },
        }),
        GetNTSPCase: build.mutation<{ FormData: Array<{ key: string; Value: Object }> }, { caseId: string; FormGuid: string }>({//FP-14022 add appguid parameter
            query: ({ caseId, FormGuid }) => ({
                url: `/ActivePanel/GetCaseDetails?caseId=${caseId}&FormGuid=${FormGuid}`,
                method: 'GET',
            }),
            transformResponse: (response: any) => {
                return response.data
            },
        }),
        SaveNTSPCase: build.mutation<any, { FormData: Array<{ key: string; Value: Object }>,FormGuid: string }>({
            query: (body) => ({
                url: '/ActivePanel/SaveNTSPCase',
                method: 'POST',
                body: body, // body is { FormData: List<KeyValue><key, Value>> }
            }),
            // transformResponse: (response: any) => {
            //     return response.data;
            // },
        }),
        GenerateCaseGuid: build.mutation<ApiDTO_NTSPform, void>({
            query: (body) => ({
                url: '/ActivePanel/GenerateCaseGuid',
                method: 'GET',
            }),
            transformResponse: (response: any) => {
                return response.data
            },
        }),
        GetComments: build.mutation<DTO_NTSPCaseNote[], string>({
            query: (caseId) => ({
                url: `/ActivePanel/GetCaseComments?caseId=${caseId}`,
                method: 'GET',
            }),
            transformResponse: (response: any) => {
                return response.data
            },
        }),
        SaveComments: build.mutation<DTO_NTSPCaseNote, Partial<DTO_NTSPCaseNote>>({
            query: (body) => ({
                url: '/ActivePanel/SaveComments',
                method: 'POST',
                body: body,
            }),
            transformResponse: (response: any) => {
                return response.data
            },
        }),
        LinkCaseToParent: build.mutation<LinkCaseResponse, LinkCaseRequest>({
            query: (body) => ({
                url: '/ActivePanel/LinkCaseToParent',
                method: 'PUT',
                body: body,
            }),
            transformResponse: (response: any) => {
                return response.data
            },
        }),
        MergeCases: build.mutation<MergeCaseResponse, MergeCaseRequest>({
            query: (body) => ({
                url: '/ActivePanel/MergeCases',
                method: 'PUT',
                body: body,
            }),
            transformResponse: (response: any) => {
                return response.data
            },
        }),
        // GetOCV: build.mutation<any, any>({
        //     query: ({key, procId}) => ({
        //         url: '/Object/'+ procId,
        //         method: 'POST',
        //         body:{key}
        //     }),
        //     transformResponse: (response: any) => {
        //         return response.data;
        //     },
        // }),
    }),
})

export const {
    useGetNTSPElementsMutation,
    useGetNTSPCaseMutation,
    useSaveNTSPCaseMutation,
    useGenerateCaseGuidMutation,
    useGetCommentsMutation,
    useSaveCommentsMutation,
    useLinkCaseToParentMutation,
    useMergeCasesMutation,
} = NTSPForm
// export const registerFormSlice;
export const { setTimezone, setWasCompletedOnLoad } = ntspSlice.actions
export default ntspSlice.reducer
